<template>
  <!--
    Página de seguridad y privacidad (/security). La referencia que se manda al
    equipo de IT de un cliente: qué IA se usa, qué ve, dónde viven los datos,
    quiénes son los proveedores y qué se ofrece a empresas. La home lleva un
    resumen (SecuritySection); aquí está todo.
  -->
  <div class="secpage">
    <!-- ============================ HERO ============================ -->
    <section class="secpage-hero">
      <div class="secpage-glow" aria-hidden="true"></div>
      <div class="lp-container secpage-hero-inner">
        <div class="lp-eyebrow"><span class="lp-eyebrow__dot"></span>{{ $t('security.eyebrow') }}</div>
        <h1 class="lp-h1 secpage-title lp-balance">
          {{ p.heroTitle }}
          <span class="lp-accent-text">{{ p.heroHighlight }}</span>
        </h1>
        <p class="lp-lead secpage-lead lp-balance">{{ p.heroSubtitle }}</p>

        <security-diagram class="secpage-diagram"></security-diagram>
      </div>
    </section>

    <hr class="lp-hr" />

    <!-- ======================= GARANTÍAS BASE ======================= -->
    <section class="lp-section lp-section--tight">
      <div class="lp-container">
        <h2 class="lp-h2 secpage-h2 lp-balance">{{ p.pillarsTitle }}</h2>
        <div class="secpage-grid">
          <div v-for="(card, i) in cards" :key="i" class="secpage-card">
            <div class="secpage-icon">
              <l-icon size="20" color="primary">{{ card.icon }}</l-icon>
            </div>
            <h4 class="secpage-card-title">{{ card.t }}</h4>
            <p class="secpage-card-desc">{{ card.d }}</p>
          </div>
        </div>
      </div>
    </section>

    <hr class="lp-hr" />

    <!-- ======================= QUÉ VE LA IA ======================= -->
    <section class="lp-section lp-section--tight">
      <div class="lp-container">
        <h2 class="lp-h2 secpage-h2 lp-balance">{{ p.aiTitle }}</h2>
        <p class="lp-lead secpage-sublead lp-balance">{{ p.aiSubtitle }}</p>
        <div class="secpage-steps">
          <div v-for="(step, i) in p.aiSteps" :key="i" class="secpage-step">
            <span class="secpage-step-num">{{ i + 1 }}</span>
            <div class="secpage-step-icon">
              <l-icon size="20" color="primary">{{ ['mouse-pointer-click', 'send', 'graduation-cap'][i] }}</l-icon>
            </div>
            <h4 class="secpage-card-title">{{ step.t }}</h4>
            <p class="secpage-card-desc">{{ step.d }}</p>
          </div>
        </div>
      </div>
    </section>

    <hr class="lp-hr" />

    <!-- ======================== PROVEEDORES ======================== -->
    <section class="lp-section lp-section--tight">
      <div class="lp-container secpage-narrow">
        <h2 class="lp-h2 secpage-h2 lp-balance">{{ p.provTitle }}</h2>
        <p class="lp-lead secpage-sublead lp-balance">{{ p.provSubtitle }}</p>
        <div class="secpage-table lp-frame">
          <div class="secpage-trow secpage-trow--head">
            <span></span>
            <span>{{ p.provPurpose }}</span>
            <span>{{ p.provSees }}</span>
            <span>{{ p.provSafeguard }}</span>
          </div>
          <div v-for="(row, i) in p.provRows" :key="i" class="secpage-trow">
            <span class="secpage-tname">{{ row.name }}</span>
            <span>{{ row.purpose }}</span>
            <span>{{ row.sees }}</span>
            <span class="secpage-tsafe"><l-icon size="13" color="primary">shield-check</l-icon>{{ row.safeguard }}</span>
          </div>
        </div>
      </div>
    </section>

    <hr class="lp-hr" />

    <!-- ========================= EMPRESAS ========================= -->
    <section class="lp-section lp-section--tight secpage-ent">
      <div class="lp-container">
        <h2 class="lp-h2 secpage-h2 lp-balance">{{ p.entTitle }}</h2>
        <p class="lp-lead secpage-sublead lp-balance">{{ p.entSubtitle }}</p>
        <div class="secpage-grid secpage-grid--3">
          <div v-for="(item, i) in p.entItems" :key="i" class="secpage-card">
            <div class="secpage-icon">
              <l-icon size="20" color="primary">{{ ['landmark', 'file-check-2', 'building-2'][i] }}</l-icon>
            </div>
            <h4 class="secpage-card-title">{{ item.t }}</h4>
            <p class="secpage-card-desc">{{ item.d }}</p>
          </div>
        </div>
      </div>
    </section>

    <hr class="lp-hr" />

    <!-- =========================== FAQ =========================== -->
    <section class="lp-section lp-section--tight">
      <div class="lp-container secpage-narrow">
        <h2 class="lp-h2 secpage-h2 lp-balance">{{ p.faqTitle }}</h2>
        <div class="secpage-faq">
          <div v-for="(item, i) in p.faq" :key="i" class="secpage-faq-item">
            <h4 class="secpage-faq-q">{{ item.q }}</h4>
            <p class="secpage-faq-a">{{ item.a }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- =========================== CTA =========================== -->
    <section class="secpage-cta">
      <div class="lp-container secpage-cta-inner">
        <h2 class="lp-h2 lp-balance">{{ p.ctaTitle }}</h2>
        <p class="lp-lead secpage-cta-lead lp-balance">{{ p.ctaSubtitle }}</p>
        <div class="secpage-cta-actions">
          <a class="lp-btn lp-btn--primary lp-btn--lg" href="mailto:hola@nukliapp.com">
            <l-icon small color="white">mail</l-icon>
            {{ p.ctaButton }}
          </a>
          <router-link class="lp-btn lp-btn--ghost lp-btn--lg" to="/politics/privaci">
            {{ p.ctaPrivacy }}
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import LIcon from '@/components/LIcon.vue';
import SecurityDiagram from '@/components/security/SecurityDiagram.vue';

export default {
  name: 'SecurityPage',
  components: { LIcon, SecurityDiagram },
  metaInfo() {
    return {
      title: this.p.metaTitle,
      meta: [
        { name: 'description', content: this.p.metaDescription },
        { property: 'og:title', content: this.p.metaTitle },
        { property: 'og:description', content: this.p.metaDescription },
      ],
    };
  },
  computed: {
    p() {
      return this.$t('security.page');
    },
    cards() {
      const items = this.$t('security.cards');
      // Mismo orden e iconos que en SecuritySection (home).
      const icons = ['lock-keyhole', 'scan-search', 'globe', 'sparkles', 'key-round', 'scroll-text'];
      return items.map((c, i) => ({ ...c, icon: icons[i] || 'shield-check' }));
    },
  },
};
</script>

<style scoped>
.secpage { background: var(--lp-bg); }

/* ---- hero ---- */
.secpage-hero {
  position: relative;
  padding-top: 128px;
  padding-bottom: 88px;
  overflow: hidden;
}
.secpage-glow {
  position: absolute;
  top: -160px;
  left: 50%;
  transform: translateX(-50%);
  width: 1100px;
  height: 560px;
  background: radial-gradient(60% 60% at 50% 0%, var(--lp-accent-weak) 0%, rgba(0, 0, 0, 0) 70%);
  pointer-events: none;
  z-index: 0;
}
.secpage-hero-inner {
  position: relative;
  z-index: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.secpage-title {
  max-width: 20ch;
  margin: 22px 0 24px;
}
.secpage-title .lp-accent-text { display: block; }
.secpage-lead {
  max-width: 640px;
  margin-bottom: 56px;
}
.secpage-diagram {
  width: 100%;
  max-width: 1020px;
}

/* ---- secciones ---- */
.secpage-h2 {
  text-align: center;
  margin-bottom: 18px;
}
.secpage-sublead {
  max-width: 620px;
  margin: 0 auto 56px;
  text-align: center;
}
.secpage-narrow { max-width: 880px; }

.secpage-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 56px;
}
.secpage-grid--3 { margin-top: 0; }
.secpage-card {
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius);
  padding: 26px 24px;
  box-shadow: var(--lp-shadow-sm);
}
.secpage-icon {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--lp-accent-weak);
  margin-bottom: 18px;
}
.secpage-card-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--lp-ink);
  margin: 0 0 8px;
}
.secpage-card-desc {
  font-size: 0.925rem;
  line-height: 1.55;
  color: var(--lp-muted);
  margin: 0;
}

/* ---- pasos IA ---- */
.secpage-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}
.secpage-step {
  position: relative;
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius);
  padding: 26px 24px;
  box-shadow: var(--lp-shadow-sm);
}
.secpage-step-num {
  position: absolute;
  top: 22px;
  right: 22px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--lp-line-strong);
  line-height: 1;
}
.secpage-step-icon {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--lp-accent-weak);
  margin-bottom: 18px;
}

/* ---- tabla de proveedores ---- */
.secpage-table {
  background: var(--lp-surface);
  overflow: hidden;
}
.secpage-trow {
  display: grid;
  grid-template-columns: 1.1fr 1.2fr 1.4fr 1.1fr;
  gap: 14px;
  align-items: center;
  padding: 15px 22px;
  border-bottom: 1px solid var(--lp-line);
  font-size: 0.9rem;
  color: var(--lp-ink-2);
}
.secpage-trow:last-child { border-bottom: 0; }
.secpage-trow--head {
  background: var(--lp-bg-subtle);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--lp-muted);
}
.secpage-tname {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  color: var(--lp-ink);
}
.secpage-tsafe {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

/* ---- empresas ---- */
.secpage-ent { background: var(--lp-bg-subtle); }

/* ---- FAQ ---- */
.secpage-faq {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.secpage-faq-item {
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius);
  padding: 22px 26px;
}
.secpage-faq-q {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--lp-ink);
  margin: 0 0 8px;
}
.secpage-faq-a {
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--lp-muted);
  margin: 0;
}

/* ---- CTA ---- */
.secpage-cta {
  padding: 120px 0;
  background: var(--lp-bg-subtle);
  border-top: 1px solid var(--lp-line);
  text-align: center;
}
.secpage-cta-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.secpage-cta-lead {
  max-width: 520px;
  margin: 20px 0 32px;
}
.secpage-cta-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

@media (max-width: 959px) {
  .secpage-grid,
  .secpage-steps { grid-template-columns: 1fr; }
  /* Tabla compacta: se cae la columna "qué ve"; el nombre ocupa toda la fila
     y debajo quedan finalidad | garantía, alineadas con la cabecera. */
  .secpage-trow { grid-template-columns: 1fr 1fr; gap: 6px 14px; }
  .secpage-trow span:nth-child(3) { display: none; }
  .secpage-trow--head span:first-child { display: none; }
  .secpage-trow .secpage-tname { grid-column: 1 / -1; }
}
@media (max-width: 599px) {
  .secpage-hero { padding-top: 104px; padding-bottom: 56px; }
  .secpage-cta { padding: 80px 0; }
}
</style>
